import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Trash2,
  Eye,
  Calendar,
  User,
  CheckCircle,
  Clock,
  AlertCircle,
  Mail,
  QrCode,
  Send,
  MapPin
} from 'lucide-react';

const InspectionManagement = () => {
  const [inspectionRequests, setInspectionRequests] = useState([]);
  const [pendingInspections, setPendingInspections] = useState([]);
  const [completedChecklists, setCompletedChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('requests'); // 'requests', 'pending', or 'completed'
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteRequestModal, setShowDeleteRequestModal] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState(null);
  const [showDistrictEmailModal, setShowDistrictEmailModal] = useState(false);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [emailDates, setEmailDates] = useState(['2024-10-24', '2024-10-25', '2024-10-26']);
  const [showQRModal, setShowQRModal] = useState(false);
  const [selectedRequestForQR, setSelectedRequestForQR] = useState(null);
  const [qrFormData, setQRFormData] = useState({
    teamEmail: '',
    additionalNotes: '',
    qrCodeGenerated: false,
    qrCodeUrl: '',
    qrCodeDataUrl: ''
  });

  // Fetch functions
  const fetchInspectionRequests = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter && { status: statusFilter })
      });

      const response = await fetch(`http://localhost:5000/api/admin/inspection-requests?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setInspectionRequests(data.requests || []);
        setTotalPages(data.totalPages || 1);
      } else {
        throw new Error('Failed to fetch inspection requests');
      }
    } catch (error) {
      console.error('Error fetching inspection requests:', error);
      setInspectionRequests([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, statusFilter]);

  const fetchPendingInspections = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:5000/api/admin/pending-inspections', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPendingInspections(data || []);
      } else {
        throw new Error('Failed to fetch pending inspections');
      }
    } catch (error) {
      console.error('Error fetching pending inspections:', error);
      setPendingInspections([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCompletedChecklists = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:5000/api/admin/completed-checklists', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCompletedChecklists(data || []);
      } else {
        throw new Error('Failed to fetch completed checklists');
      }
    } catch (error) {
      console.error('Error fetching completed checklists:', error);
      setCompletedChecklists([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Effects
  useEffect(() => {
    if (activeTab === 'requests') {
      fetchInspectionRequests();
    } else if (activeTab === 'pending') {
      fetchPendingInspections();
    } else if (activeTab === 'completed') {
      fetchCompletedChecklists();
    }
  }, [activeTab, fetchInspectionRequests, fetchPendingInspections, fetchCompletedChecklists]);

  useEffect(() => {
    if (activeTab === 'requests') {
      fetchInspectionRequests();
    }
  }, [activeTab, currentPage, searchTerm, statusFilter, fetchInspectionRequests]);

  // Helper functions for status icons and colors

  const getRequestStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'SCHEDULED':
        return <Calendar className="w-4 h-4 text-blue-500" />;
      case 'CONTACTED':
        return <User className="w-4 h-4 text-orange-500" />;
      case 'PENDING_ADMIN_REVIEW':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'CANCELLED':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRequestStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED':
        return 'bg-green-100 text-green-800';
      case 'SCHEDULED':
        return 'bg-blue-100 text-blue-800';
      case 'CONTACTED':
        return 'bg-orange-100 text-orange-800';
      case 'PENDING_ADMIN_REVIEW':
        return 'bg-yellow-100 text-yellow-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // QR Code functions
  const handleQRFormChange = (field, value) => {
    setQRFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSendQRCode = async () => {
    if (!qrFormData.teamEmail) {
      alert('Please enter the team member email');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const qrCodeId = `inspection_${selectedRequestForQR._id}_${Date.now()}`;
      
      const qrCodeData = {
        requestId: selectedRequestForQR._id,
        teamEmail: qrFormData.teamEmail,
        inspectionDate: selectedRequestForQR.preferredDate,
        inspectionTime: selectedRequestForQR.preferredTime,
        additionalNotes: qrFormData.additionalNotes,
        customerInfo: {
          name: selectedRequestForQR.userName,
          email: selectedRequestForQR.userEmail,
          phone: selectedRequestForQR.contactNumber,
          subscription: selectedRequestForQR.subscriptionType,
          address: 'Property address from customer confirmation' // Placeholder since address not stored in InspectionRequest
        },
        qrCodeId: qrCodeId
      };

      const response = await fetch('http://localhost:5000/api/admin/send-qr-code', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(qrCodeData)
      });

      if (response.ok) {
        const result = await response.json();
        
        setQRFormData(prev => ({
          ...prev,
          qrCodeGenerated: true,
          qrCodeUrl: result.qrCodeUrl || `${window.location.origin}/inspection-checklist/${qrCodeId}`,
          qrCodeDataUrl: result.qrCodeDataUrl || ''
        }));

        alert('QR Code generated and email sent successfully!');
        
        setTimeout(() => {
          setShowQRModal(false);
          setSelectedRequestForQR(null);
          setQRFormData({
            teamEmail: '',
            additionalNotes: '',
            qrCodeGenerated: false,
            qrCodeUrl: '',
            qrCodeDataUrl: ''
          });
        }, 3000);
      } else {
        throw new Error('Failed to send QR code');
      }
    } catch (error) {
      console.error('Error sending QR code:', error);
      alert('Failed to send QR code. Please try again.');
    }
  };

  // Other handler functions
  const handleDeleteRequest = async () => {
    if (!requestToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`http://localhost:5000/api/admin/inspection-requests/${requestToDelete._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Inspection request deleted successfully');
        fetchInspectionRequests();
        setShowDeleteRequestModal(false);
        setRequestToDelete(null);
      } else {
        throw new Error('Failed to delete inspection request');
      }
    } catch (error) {
      console.error('Error deleting inspection request:', error);
      alert('Failed to delete inspection request');
    }
  };

  const handleSendDistrictEmails = async () => {
    if (!selectedDistrict || emailDates.filter(date => date).length === 0) {
      alert('Please select at least one available date');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:5000/api/admin/send-district-emails', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          district: selectedDistrict.district,
          requests: selectedDistrict.requests,
          availableDates: emailDates.filter(date => date)
        })
      });

      if (response.ok) {
        const result = await response.json();
        const { summary } = result;
        
        let message = `District emails sent successfully!\n\n`;
        message += `📧 Emails sent: ${summary.emailsSent}\n`;
        if (summary.emailsFailed > 0) {
          message += `❌ Emails failed: ${summary.emailsFailed}\n`;
        }
        if (summary.emailsSkipped > 0) {
          message += `⏭️ Emails skipped: ${summary.emailsSkipped}\n`;
        }
        if (summary.recordsDeleted > 0) {
          message += `🗑️ Pending records deleted: ${summary.recordsDeleted}`;
        }
        
        alert(message);
        
        // Refresh the pending inspections list to show updated data
        fetchPendingInspections();
        
        setShowDistrictEmailModal(false);
        setSelectedDistrict(null);
        setEmailDates(['2024-10-24', '2024-10-25', '2024-10-26']);
      } else {
        throw new Error('Failed to send district emails');
      }
    } catch (error) {
      console.error('Error sending district emails:', error);
      alert('Failed to send district emails. Please try again.');
    }
  };

  const handleCleanupEmptyRecords = async () => {
    if (!window.confirm('Are you sure you want to remove all empty/invalid inspection records? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch('http://localhost:5000/api/admin/cleanup-empty-requests', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Cleanup completed! Removed ${result.deletedCount} empty records.`);
        // Refresh the inspection requests
        fetchInspectionRequests();
      } else {
        throw new Error('Failed to cleanup empty records');
      }
    } catch (error) {
      console.error('Error cleaning up empty records:', error);
      alert('Failed to cleanup empty records. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 -mt-8">
      {/* Header Section with Gradient */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg py-20 pt-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <CheckCircle className="w-8 h-8" />
                Inspection Management
              </h1>
              <p className="text-orange-100 mt-2 text-lg">
                Manage inspection requests, pending inspections, and completed checklists
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-orange-200 overflow-hidden"
        >

        <div className="p-6">
          {/* Tab Navigation */}
          <div className="flex space-x-2 mb-8 border-b border-gray-200 pb-2">
            <button
              onClick={() => setActiveTab('requests')}
              className={`px-6 py-3 rounded-t-lg font-semibold transition-all duration-200 ${
                activeTab === 'requests'
                  ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Inspection Requests
              </div>
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-6 py-3 rounded-t-lg font-semibold transition-all duration-200 ${
                activeTab === 'pending'
                  ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Pending Inspections
              </div>
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`px-6 py-3 rounded-t-lg font-semibold transition-all duration-200 ${
                activeTab === 'completed'
                  ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-md'
                  : 'text-gray-600 hover:text-orange-600 hover:bg-orange-50'
              }`}
            >
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Completed Checklists
              </div>
            </button>
          </div>

          {/* Search and Filters for Requests Tab */}
          {activeTab === 'requests' && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-6 mb-6 shadow-sm"
            >
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-orange-500 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search by customer name or email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border-2 border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white shadow-sm transition-all"
                    />
                  </div>
                </div>
                <div className="sm:w-56">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="w-full px-4 py-3 border-2 border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white font-medium text-gray-700 shadow-sm transition-all"
                  >
                    <option value="">All Statuses</option>
                    <option value="PENDING_ADMIN_REVIEW">Pending Review</option>
                    <option value="CONTACTED">Contacted</option>
                    <option value="SCHEDULED">Scheduled</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>
                <button
                  onClick={handleCleanupEmptyRecords}
                  className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 flex items-center gap-2 font-semibold shadow-md hover:shadow-lg"
                  title="Remove empty/invalid records"
                >
                  <Trash2 className="w-5 h-5" />
                  Cleanup
                </button>
              </div>
            </motion.div>
          )}

          {/* Content based on active tab */}
          {activeTab === 'requests' && (
            <InspectionRequestsTable
              requests={inspectionRequests}
              loading={loading}
              onDelete={(request) => {
                setRequestToDelete(request);
                setShowDeleteRequestModal(true);
              }}
              onSendQR={(request) => {
                setSelectedRequestForQR(request);
                setShowQRModal(true);
              }}
              getRequestStatusIcon={getRequestStatusIcon}
              getRequestStatusColor={getRequestStatusColor}
            />
          )}

          {activeTab === 'pending' && (
            <PendingInspectionsTable
              inspections={pendingInspections}
              loading={loading}
              onSendDistrictEmail={(district) => {
                setSelectedDistrict(district);
                setShowDistrictEmailModal(true);
              }}
            />
          )}

          {activeTab === 'completed' && (
            <CompletedChecklistsTable
              checklists={completedChecklists}
              loading={loading}
            />
          )}

          {/* Pagination for Requests */}
          {activeTab === 'requests' && totalPages > 1 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-8 flex justify-center"
            >
              <nav className="flex items-center gap-3">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-5 py-2.5 border-2 border-orange-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-50 hover:border-orange-300 transition-all font-medium text-gray-700 shadow-sm"
                >
                  Previous
                </button>
                <span className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-semibold shadow-md">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-5 py-2.5 border-2 border-orange-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-50 hover:border-orange-300 transition-all font-medium text-gray-700 shadow-sm"
                >
                  Next
                </button>
              </nav>
            </motion.div>
          )}
        </div>

        {/* QR Code Modal - ONLY MODAL KEPT */}
        {showQRModal && selectedRequestForQR && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl max-w-md w-full shadow-2xl border-2 border-orange-200 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-6 py-4 text-white">
                <div className="flex items-center gap-3">
                  <QrCode className="w-7 h-7" />
                  <h2 className="text-2xl font-bold">Send QR Code to Team</h2>
                </div>
              </div>

              <div className="p-6">
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 border-2 border-purple-200 p-5 rounded-xl mb-6 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-600" />
                    Inspection Details
                  </h3>
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium">Customer:</span> {selectedRequestForQR.userName}</p>
                    <p><span className="font-medium">Email:</span> {selectedRequestForQR.userEmail}</p>
                    <p><span className="font-medium">Contact:</span> {selectedRequestForQR.contactNumber}</p>
                    <p><span className="font-medium">Subscription:</span> {selectedRequestForQR.subscriptionType}</p>
                    <p><span className="font-medium">Scheduled Date:</span> {new Date(selectedRequestForQR.preferredDate).toLocaleDateString()}</p>
                    <p><span className="font-medium">Scheduled Time:</span> {selectedRequestForQR.preferredTime}</p>
                    {selectedRequestForQR.specialInstructions && (
                      <p><span className="font-medium">Instructions:</span> {selectedRequestForQR.specialInstructions}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Team Member Email *
                    </label>
                    <input
                      type="email"
                      value={qrFormData.teamEmail}
                      onChange={(e) => handleQRFormChange('teamEmail', e.target.value)}
                      placeholder="team@company.com"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Notes (Optional)
                    </label>
                    <textarea
                      value={qrFormData.additionalNotes}
                      onChange={(e) => handleQRFormChange('additionalNotes', e.target.value)}
                      placeholder="Any special instructions for the team..."
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {/* QR Code Display Section */}
                {qrFormData.qrCodeGenerated && qrFormData.qrCodeDataUrl && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-medium text-green-800 mb-3 flex items-center">
                      <QrCode className="w-5 h-5 mr-2" />
                      QR Code Generated Successfully
                    </h4>
                    
                    <div className="text-center">
                      <img 
                        src={qrFormData.qrCodeDataUrl} 
                        alt="QR Code for Team Access"
                        className="mx-auto mb-3 border border-gray-300 rounded-lg p-2 bg-white"
                        style={{ maxWidth: '200px' }}
                      />
                      
                      <div className="space-y-2">
                        <p className="text-sm text-green-700">
                          Share this QR code with your team member, or use the link below:
                        </p>
                        <div className="bg-white p-2 rounded border text-xs break-all font-mono">
                          {qrFormData.qrCodeUrl}
                        </div>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(qrFormData.qrCodeUrl);
                            alert('Link copied to clipboard!');
                          }}
                          className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                        >
                          Copy Link
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-700">
                    <strong>What happens next:</strong>
                  </p>
                  <ul className="text-xs text-blue-600 mt-1 space-y-1">
                    <li>• Team member will receive an email with QR code</li>
                    <li>• QR code links to inspection checklist form</li>
                    <li>• Team can complete checklist during site visit</li>
                    <li>• You'll get notified when checklist is submitted</li>
                    <li>• QR code expires in 7 days</li>
                  </ul>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowQRModal(false);
                    setSelectedRequestForQR(null);
                    setQRFormData({
                      teamEmail: '',
                      additionalNotes: '',
                      qrCodeGenerated: false,
                      qrCodeUrl: '',
                      qrCodeDataUrl: ''
                    });
                  }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 font-semibold transition-all shadow-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendQRCode}
                  disabled={!qrFormData.teamEmail}
                  className={`flex-1 px-6 py-3 rounded-lg font-semibold shadow-md transition-all duration-200 ${
                    qrFormData.teamEmail
                      ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white hover:from-purple-700 hover:to-purple-800 hover:shadow-lg'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  <div className="flex items-center justify-center space-x-2">
                    <Send className="w-5 h-5" />
                    <span>Send QR Code</span>
                  </div>
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Delete Request Modal */}
        {showDeleteRequestModal && requestToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl max-w-md w-full shadow-2xl border-2 border-red-200 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-4 text-white">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-7 h-7" />
                  <h2 className="text-2xl font-bold">Delete Inspection Request</h2>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-700 mb-6 text-lg">
                  Are you sure you want to delete this inspection request? This action cannot be undone.
                </p>
                <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-200 p-5 rounded-xl shadow-sm">
                  <h3 className="font-medium text-gray-900 mb-2">Request Details</h3>
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium">Customer:</span> {requestToDelete.userName}</p>
                    <p><span className="font-medium">Email:</span> {requestToDelete.userEmail}</p>
                    <p><span className="font-medium">Subscription:</span> {requestToDelete.subscriptionType}</p>
                    <p><span className="font-medium">Preferred Date:</span> {requestToDelete.preferredDate}</p>
                  </div>
                </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200 mt-6">
                <button
                  onClick={() => {
                    setShowDeleteRequestModal(false);
                    setRequestToDelete(null);
                  }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 font-semibold transition-all shadow-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteRequest}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                >
                  Delete Request
                </button>
              </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* District Email Modal */}
        {showDistrictEmailModal && selectedDistrict && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-xl max-w-md w-full shadow-2xl border-2 border-orange-200 overflow-hidden"
            >
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 text-white">
                <div className="flex items-center gap-3">
                  <Mail className="w-7 h-7" />
                  <h2 className="text-2xl font-bold">Send District Email</h2>
                </div>
              </div>

              <div className="p-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 border-2 border-blue-200 p-5 rounded-xl mb-6 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    District Details
                  </h3>
                  <div className="text-sm space-y-1">
                    <p><span className="font-medium">District:</span> {selectedDistrict.district}</p>
                    <p><span className="font-medium">Properties:</span> {selectedDistrict.requests.length} properties</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Available Date 1
                    </label>
                    <input
                      type="date"
                      value={emailDates[0]}
                      onChange={(e) => {
                        const newDates = [...emailDates];
                        newDates[0] = e.target.value;
                        setEmailDates(newDates);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Available Date 2
                    </label>
                    <input
                      type="date"
                      value={emailDates[1]}
                      onChange={(e) => {
                        const newDates = [...emailDates];
                        newDates[1] = e.target.value;
                        setEmailDates(newDates);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Available Date 3
                    </label>
                    <input
                      type="date"
                      value={emailDates[2]}
                      onChange={(e) => {
                        const newDates = [...emailDates];
                        newDates[2] = e.target.value;
                        setEmailDates(newDates);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowDistrictEmailModal(false);
                    setSelectedDistrict(null);
                    setEmailDates(['2024-10-24', '2024-10-25', '2024-10-26']);
                  }}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 font-semibold transition-all shadow-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendDistrictEmails}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <Send className="w-5 h-5" />
                  Send Emails
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
      </div>
    </div>
  );
};

// Component for inspection requests table
const InspectionRequestsTable = ({ requests, loading, onDelete, onSendQR, getRequestStatusIcon, getRequestStatusColor }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="bg-white rounded-xl shadow-md overflow-hidden border border-orange-100"
  >
    {loading ? (
      <div className="p-12 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent mx-auto mb-4"></div>
        <p className="text-gray-600 text-lg font-medium">Loading inspection requests...</p>
      </div>
    ) : requests.length === 0 ? (
      <div className="p-12 text-center">
        <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">No Inspection Requests</h3>
        <p className="text-gray-600">No inspection requests found matching your criteria.</p>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-orange-50 to-orange-100 border-b-2 border-orange-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold text-orange-900 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-orange-900 uppercase tracking-wider">
                Property Address
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-orange-900 uppercase tracking-wider">
                Subscription
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-orange-900 uppercase tracking-wider">
                Preferred Schedule
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-orange-900 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-orange-900 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-orange-900 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {requests.map((request) => (
              <tr key={request._id} className="hover:bg-orange-50 transition-colors duration-150">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {request.userName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {request.userEmail}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {request.propertyAddress || 'Not provided'}
                  </div>
                  <div className="text-sm text-gray-500">
                    {request.district || 'District not specified'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{request.subscriptionType}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {request.preferredDate ? new Date(request.preferredDate).toLocaleDateString() : 'Not specified'}
                  </div>
                  <div className="text-sm text-gray-500">
                    {request.preferredTimeSlot || 'Any time'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{request.contactNumber}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRequestStatusColor(request.status)}`}>
                    {getRequestStatusIcon(request.status)}
                    <span className="ml-1">{request.status.replace('_', ' ')}</span>
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-3">
                    <button
                      onClick={() => onSendQR(request)}
                      className="p-2 text-purple-600 hover:text-purple-900 hover:bg-purple-100 rounded-lg transition-all duration-200"
                      title="Send QR Code"
                    >
                      <QrCode className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => onDelete(request)}
                      className="p-2 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-lg transition-all duration-200"
                      title="Delete Request"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </motion.div>
);

// Component for pending inspections table (subscription-based)
const PendingInspectionsTable = ({ inspections, loading, onSendDistrictEmail }) => {
  const groupedByDistrict = inspections.reduce((acc, inspection) => {
    const district = inspection.district || 'Unknown District';
    if (!acc[district]) {
      acc[district] = [];
    }
    acc[district].push(inspection);
    return acc;
  }, {});

  const districts = Object.keys(groupedByDistrict).map(district => ({
    district,
    requests: groupedByDistrict[district]
  }));

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-white rounded-xl shadow-md overflow-hidden border border-orange-100"
    >
      {loading ? (
        <div className="p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading pending inspections...</p>
        </div>
      ) : districts.length === 0 ? (
        <div className="p-12 text-center">
          <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No Pending Inspections</h3>
          <p className="text-gray-600">No pending subscription-based inspections found.</p>
        </div>
      ) : (
        <div className="p-6">
          <div className="mb-8">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-200 p-6 rounded-xl shadow-sm">
              <h3 className="text-lg font-medium text-gray-900">Pending Inspections Summary</h3>
              <p className="text-sm text-gray-600 mt-1">
                Subscription-based properties requiring inspection scheduling
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {districts.map((districtData) => (
              <motion.div
                key={districtData.district}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="border-2 border-orange-200 rounded-xl p-6 bg-gradient-to-br from-white to-orange-50 shadow-sm hover:shadow-md transition-all"
              >
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                      <MapPin className="w-6 h-6 text-orange-600" />
                      {districtData.district}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1 font-medium">{districtData.requests.length} properties</p>
                  </div>
                  <button
                    onClick={() => onSendDistrictEmail(districtData)}
                    className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 flex items-center space-x-2 font-semibold shadow-md hover:shadow-lg transition-all duration-200"
                  >
                    <Mail className="w-5 h-5" />
                    <span>Send District Email</span>
                  </button>
                </div>

                <div className="overflow-x-auto rounded-lg border-2 border-orange-100">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-orange-100 to-orange-200">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-bold text-orange-900 uppercase tracking-wider">
                          Customer
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-orange-900 uppercase tracking-wider">
                          Address
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-orange-900 uppercase tracking-wider">
                          Subscription
                        </th>
                        <th className="px-5 py-3 text-left text-xs font-bold text-orange-900 uppercase tracking-wider">
                          Contact
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-orange-100">
                      {districtData.requests.map((inspection) => (
                        <tr key={inspection._id} className="hover:bg-orange-50 transition-colors duration-150">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {inspection.customer?.firstName || 'Customer'} {inspection.customer?.lastName || 'Name'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {inspection.customer?.email || 'No email provided'}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {inspection.propertyAddress || 'Address not provided'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {inspection.district || 'District not specified'}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {inspection.subscriptionTier || 'Not specified'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {inspection.propertyType || 'Property type not specified'}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {inspection.customer?.phone || 'No phone provided'}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

// Component for completed checklists table
const CompletedChecklistsTable = ({ checklists, loading }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="bg-white rounded-xl shadow-md overflow-hidden border border-orange-100"
  >
    {loading ? (
      <div className="p-12 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent mx-auto mb-4"></div>
        <p className="text-gray-600 text-lg font-medium">Loading completed checklists...</p>
      </div>
    ) : checklists.length === 0 ? (
      <div className="p-12 text-center">
        <CheckCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">No Completed Checklists</h3>
        <p className="text-gray-600">No completed inspection checklists found.</p>
      </div>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-green-50 to-green-100 border-b-2 border-green-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold text-green-900 uppercase tracking-wider">
                Customer
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-green-900 uppercase tracking-wider">
                Inspection Date
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-green-900 uppercase tracking-wider">
                Inspector
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-green-900 uppercase tracking-wider">
                Score
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-green-900 uppercase tracking-wider">
                Completed
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-green-900 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {checklists.map((checklist) => (
              <tr key={checklist._id} className="hover:bg-green-50 transition-colors duration-150">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {checklist.customerName}
                      </div>
                      <div className="text-sm text-gray-500">
                        {checklist.customerEmail}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {checklist.inspectionDate ? new Date(checklist.inspectionDate).toLocaleDateString() : 'Not specified'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{checklist.inspectorEmail}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {checklist.score ? `${checklist.score}%` : 'N/A'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {checklist.completedAt ? new Date(checklist.completedAt).toLocaleDateString() : 'Not completed'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => {
                      // View checklist details
                      window.open(`/admin/checklist/${checklist._id}`, '_blank');
                    }}
                    className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-lg transition-all duration-200"
                    title="View Details"
                  >
                    <Eye className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </motion.div>
);

export default InspectionManagement;